import type { RBACGroup, RBACUser } from '../../types';

import type { EffectiveUserRow } from './components/ACLEffectiveUserList';

export type EffectiveUser = Pick<
  RBACUser,
  'accountEnabled' | 'id' | 'displayName' | 'userPrincipalName'
>;

type Args = {
  // Users assigned directly to the ACL (policy drift) — keep access regardless of roles.
  directUsers?: EffectiveUser[];
  // Role groups currently assigned to the ACL (kept fresh by the submit cache patch).
  roleMembers?: RBACGroup[];
  // Role groups queued to be added / removed but not yet submitted.
  pendingAdds: RBACGroup[];
  pendingDeletes: RBACGroup[];
  // Per-role user membership, keyed by role id (getRolesUserMembers).
  roleUsers?: Record<string, EffectiveUser[]>;
};

// The effective-access rows for an ACL, derived purely from the ACL's direct users and
// the per-role user memberships — NOT from a server-side transitiveMembers read. Because
// those inputs are the same lists the submit cache patch keeps fresh, the effective set
// re-derives the instant a role is added or removed, with no eventual-consistency lag.
//
// A user is "current" if they are a direct member or a member of any assigned role; they
// are flagged `removed` (red) only when every role granting them access is queued for
// removal and they have no other path. Users a queued role-add would newly grant access
// to are appended as `pending` (green) rows. See ADR-0002 and acls route.
export function deriveEffectiveUsers({
  directUsers,
  roleMembers,
  pendingAdds,
  pendingDeletes,
  roleUsers,
}: Args): EffectiveUserRow[] {
  const directIdSet = new Set((directUsers ?? []).map((user) => user.id));
  const roleNameById = new Map<string, string>();
  [...(roleMembers ?? []), ...pendingAdds].forEach((role) =>
    roleNameById.set(role.id, role.displayName)
  );
  const assignedRoleIdSet = new Set((roleMembers ?? []).map((role) => role.id));
  const pendingRoleIdSet = new Set(pendingAdds.map((role) => role.id));
  const pendingDeleteIdSet = new Set(pendingDeletes.map((role) => role.id));

  const assignedByUser = new Map<string, Set<string>>();
  const pendingByUser = new Map<string, Set<string>>();
  // Every user we can render a row for: direct members, members of any assigned role,
  // and members a pending role-add would grant access to.
  const userById = new Map<string, EffectiveUser>();
  (directUsers ?? []).forEach((user) => userById.set(user.id, user));
  const addTo = (map: Map<string, Set<string>>, userId: string, roleId: string) => {
    let set = map.get(userId);
    if (!set) {
      set = new Set();
      map.set(userId, set);
    }
    set.add(roleId);
  };
  Object.entries(roleUsers ?? {}).forEach(([roleId, users]) => {
    users.forEach((user) => {
      if (assignedRoleIdSet.has(roleId)) {
        addTo(assignedByUser, user.id, roleId);
        userById.set(user.id, user);
      }
      if (pendingRoleIdSet.has(roleId)) {
        addTo(pendingByUser, user.id, roleId);
        userById.set(user.id, user);
      }
    });
  });

  const roleNames = (ids: Set<string> | undefined) =>
    ids
      ? Array.from(ids)
          .map((id) => roleNameById.get(id))
          .filter((name): name is string => !!name)
          .sort()
      : [];

  // Committed effective set = direct members ∪ members of any assigned role.
  const currentIdSet = new Set<string>(directIdSet);
  assignedByUser.forEach((_roles, userId) => currentIdSet.add(userId));

  const current: EffectiveUserRow[] = Array.from(currentIdSet).map((userId) => {
    const user = userById.get(userId)!;
    const direct = directIdSet.has(userId);
    const grantingRoles = assignedByUser.get(userId);
    // Loses access only if every role granting it is queued for removal, and it has
    // no other path: not direct, and not granted by a queued add.
    let removed = false;
    if (!direct && grantingRoles && grantingRoles.size > 0) {
      const remaining = Array.from(grantingRoles).filter((id) => !pendingDeleteIdSet.has(id));
      removed = remaining.length === 0 && !pendingByUser.has(userId);
    }
    return {
      accountEnabled: user.accountEnabled,
      direct,
      displayName: user.displayName,
      id: userId,
      pending: false,
      removed,
      userPrincipalName: user.userPrincipalName,
      viaRoles: roleNames(grantingRoles),
    };
  });

  const pendingRows: EffectiveUserRow[] = [];
  pendingByUser.forEach((roleIds, userId) => {
    if (currentIdSet.has(userId)) return; // already has access
    const user = userById.get(userId)!; // populated alongside pendingByUser above
    pendingRows.push({
      accountEnabled: user.accountEnabled,
      direct: false,
      displayName: user.displayName,
      id: user.id,
      pending: true,
      removed: false,
      userPrincipalName: user.userPrincipalName,
      viaRoles: roleNames(roleIds),
    });
  });

  return [...current, ...pendingRows];
}
